import { useEffect } from 'react'
import {
  formatBirthDateDisplay,
  formatEmployeeFullName,
  getEmployeeInitials,
  getEmployeePositionLabel,
} from '../utils/employeeDisplay'
import './SchoolEmployeeDetailModal.css'

export type SchoolEmployeeDetail = {
  name: string
  title?: string
  first_name?: string
  last_name?: string
  title_after?: string
  email?: string
  work_email?: string
  personal_number?: string
  birth_date?: string
  phone?: string
  role?: string
  position?: string
}

type SchoolEmployeeDetailModalProps = {
  employee: SchoolEmployeeDetail | null
  isOpen: boolean
  onClose: () => void
}

export default function SchoolEmployeeDetailModal({
  employee,
  isOpen,
  onClose,
}: SchoolEmployeeDetailModalProps) {
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [isOpen, onClose])

  if (!isOpen || !employee) return null

  const fullName = formatEmployeeFullName(employee)
  const positionLabel = getEmployeePositionLabel(employee)
  const initials = getEmployeeInitials(employee)
  const workEmail = employee.work_email || employee.email
  const birthFormatted = employee.birth_date
    ? formatBirthDateDisplay(employee.birth_date)
    : null

  return (
    <div
      className="school-employee-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="school-employee-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="school-employee-modal-title"
      >
        <button
          type="button"
          className="school-employee-modal__close"
          onClick={onClose}
          aria-label="Zavřít"
        >
          ×
        </button>

        <header className="school-employee-modal__hero">
          <span className="school-employee-modal__avatar" aria-hidden>
            {initials}
          </span>
          <div className="school-employee-modal__hero-text">
            <h2 id="school-employee-modal-title" className="school-employee-modal__name">
              {fullName}
            </h2>
            {positionLabel && (
              <p className="school-employee-modal__position">{positionLabel}</p>
            )}
          </div>
        </header>

        <div className="school-employee-modal__body">
          <dl className="school-employee-modal__grid">
            {employee.personal_number && (
              <div className="school-employee-modal__field">
                <dt>Osobní číslo</dt>
                <dd>{employee.personal_number}</dd>
              </div>
            )}
            {birthFormatted && (
              <div className="school-employee-modal__field">
                <dt>Datum narození</dt>
                <dd>{birthFormatted}</dd>
              </div>
            )}
            {employee.phone && (
              <div className="school-employee-modal__field">
                <dt>Telefon</dt>
                <dd>
                  <a href={`tel:${employee.phone}`} className="school-employee-modal__link">
                    {employee.phone}
                  </a>
                </dd>
              </div>
            )}
            {workEmail && (
              <div className="school-employee-modal__field school-employee-modal__field--wide">
                <dt>Pracovní e-mail</dt>
                <dd>
                  <a href={`mailto:${workEmail}`} className="school-employee-modal__link">
                    {workEmail}
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>

        {(employee.phone || workEmail) && (
          <footer className="school-employee-modal__footer">
            {employee.phone && (
              <a href={`tel:${employee.phone}`} className="school-employee-modal__action school-employee-modal__action--phone">
                Zavolat
              </a>
            )}
            {workEmail && (
              <a href={`mailto:${workEmail}`} className="school-employee-modal__action school-employee-modal__action--work">
                E-mail
              </a>
            )}
          </footer>
        )}
      </div>
    </div>
  )
}
